import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  CircularProgress,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Grid,
  Tooltip
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Search as SearchIcon, Edit as EditIcon } from "@mui/icons-material";
import baseURL from "../../config";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TablePagination from "@mui/material/TablePagination";
import * as XLSX from "xlsx";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";

const DepartureModal = ({ departureDetails }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [filteredDepartureDetails, setFilteredDepartureDetails] = useState([]);
  const [loadingCrewId, setLoadingCrewId] = useState(null);
  const [dgChargesData, setDgChargesData] = useState(null);
  const [openChargesDialog, setOpenChargesDialog] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editingArrivalId, setEditingArrivalId] = useState(null);
  const [newArrivalDate, setNewArrivalDate] = useState("");
  const navigate = useNavigate();

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i);

  const fetchDGCharges = async (contractId) => {
    try {
      const token = localStorage.getItem("token"); // or your auth logic
      const response = await axios.post(
        `${baseURL}/api/chargesDGSignOnSignOff/${contractId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDgChargesData(response.data);
      setOpenChargesDialog(true);
    } catch (error) {
      console.error("Error fetching DG Charges:", error);
      toast.error("Failed to load DG Charges.");
    }
  };


  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSendRequest = async (crewId) => {
    console.log("Send Request triggered for Crew ID:", crewId);
    if (!crewId) return toast.error("Crew ID not found.");

    setLoadingCrewId(crewId);

    try {
      const res = await fetch(
        `${baseURL}/api/services/send-sea-service-request/${crewId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ crewId }),
        }
      );

      if (!res.ok) throw new Error("Failed to send email.");
      toast.success("Sea Service request email sent successfully.");
    } catch (err) {
      console.error(err);
      toast.error("Error sending email.");
    } finally {
      setLoadingCrewId(null); // Reset loading state
    }
  };

  // Filter on search
  useEffect(() => {
    if (Array.isArray(departureDetails)) {
      const filtered = departureDetails.filter((item) => {
        const portName = item.contractStatusAfterForm1?.departure?.[0]?.portName?.toLowerCase() || "";
        const vesselName = item.contractDetails?.ShipDetails?.vesselname?.toLowerCase() || "";
        const agentName = item.contractDetails?.crewingAgent?.agentName?.toLowerCase() || "";
        const crewName = item.contractDetails?.crewdetails?.name?.toLowerCase() || "";
        const indos = item.contractDetails?.crewdetails?.indosno?.toLowerCase() || "";
        return portName.includes(searchQuery) || vesselName.includes(searchQuery) || agentName.includes(searchQuery) || crewName.includes(searchQuery) ||
          indos.includes(searchQuery);;
      });
      setFilteredDepartureDetails(filtered);
    } else {
      setFilteredDepartureDetails([]);
    }
  }, [departureDetails, searchQuery]);

  // console.log("filteredDepartureDetails", filteredDepartureDetails)
  const filteredCocData = filteredDepartureDetails.filter((row) => {
    const cocDateRaw = row.contractStatusAfterForm1?.departure?.[0]?.Date || row.contractStatusAfterForm1?.departure?.[0]?.date;
    if (!cocDateRaw) return false;

    const cocDate = new Date(cocDateRaw);
    const month = String(cocDate.getMonth() + 1).padStart(2, '0'); // '03' for March
    const year = String(cocDate.getFullYear());

    // console.log({ month, year, selectedMonth, selectedYear, cocDateRaw });

    return (
      (!selectedMonth || month === selectedMonth) &&
      (!selectedYear || year === String(selectedYear))
    );
  });


  // Check for approved sea service
  useEffect(() => {
    const checkApprovedSeaService = async () => {
      const updatedDetails = await Promise.all(
        filteredDepartureDetails.map(async (item) => {
          try {
            const contractId = item.contractDetails?._id;
            const response = await axios.get(
              `${baseURL}/api/services/getApprovedSeaserviceByContractId/${contractId}`
            );
            return {
              ...item,
              approvedSeaService: response.data?.approved === true,
            };
          } catch (error) {
            console.error("Error fetching approved sea service:", error);
            return { ...item, approvedSeaService: false };
          }
        })
      );
      setFilteredDepartureDetails(updatedDetails);
    };

    if (filteredDepartureDetails.length > 0) {
      checkApprovedSeaService();
    }
  }, [filteredDepartureDetails.length]);

  const handleExportToExcel = () => {
    const dataToExport = filteredCocData.map((item, index) => {
      // Departure info
      const departureDate = item.contractStatusAfterForm1?.departure?.[0]?.date;
      const formattedDepartureDate = departureDate
        ? new Date(departureDate).toLocaleDateString("en-GB")
        : "N/A";

      const departurePortName = item.contractStatusAfterForm1?.departure?.[0]?.portName || "N/A";

      const departureCreator = item.contractStatusAfterForm1?.departure?.[0]?.creatorIdWithName?.[0]?.name || "N/A";
      const departureCreatedAt = item.contractStatusAfterForm1?.departure?.[0]?.creatorIdWithName?.[0]?.createdAt;
      const formattedDepartureCreatedAt = departureCreatedAt
        ? new Date(departureCreatedAt).toLocaleDateString("en-GB").replace(/\//g, "-")
        : "N/A";

      return {
        "Sr No.": index + 1,
        "Vessel Name": item.contractDetails?.ShipDetails?.vesselname || "N/A",
        "Company": item.contractDetails?.vesselOwner?.company_shortname || "N/A",
        "Crew Name": item.contractDetails?.crewdetails?.name || "N/A",
        "Indos No.": item.contractDetails?.crewdetails?.indosno || "N/A",
        "Agent": item.contractDetails?.crewingAgent?.agentName || "N/A",
        "Port Name (Arrival)": departurePortName,
        "Arrival Date": formattedDepartureDate,
        "Sea Service Status": item.approvedSeaService
          ? "Approved Sea Service Request"
          : "Sea Service Request Not Approved",

      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Arrival_SeaService_Details");

    XLSX.writeFile(workbook, "Arrival_SeaService_Details.xlsx");
  };

  const token = localStorage.getItem("token");
  let userEmail = "";

  if (token) {
    try {
      const decoded = jwtDecode(token);
      userEmail = decoded.email;
    } catch (error) {
      console.error("Invalid token:", error);
    }
  }

  const ChangeArrivalDate = async (id) => {
    try {

      if (!newArrivalDate) {
        alert("Please select a SignOff date!");
        return;
      }

      const response = await axios.put(
        `${baseURL}/api/updateDepartureDateByEmail/${id}`,
        { newDate: newArrivalDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );


      if (response.data.success) {
        alert("✅ Arrival Date updated successfully!");
        setEditingArrivalId(null);
        setNewArrivalDate("");
        window.location.reload();

      } else {
        console.warn("⚠️ API returned success:false");
        alert("⚠️ Failed to update COC Date.");
      }
    } catch (error) {
      console.error("❌ Error updating COC Date:", error);
      console.error("❌ Error details:", error.response?.data);
      alert(error.response?.data?.message || "Internal server error");
    }
  };


  return (
    <Box mt={3} width="100%" p={2}>
      {/* Search Bar & Title */}
      <Box
        sx={{
          p: 1,
          bgcolor: "primary.main",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "nowrap",
          gap: 2,
        }}
      >
        {/* Search Bar */}
        <TextField
          placeholder="Search by Port Name"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            width: 280,
          }}
        />

        {/* Title */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ whiteSpace: "nowrap", mx: 2 }}
        >
          Arrival Details
        </Typography>

        {/* Filters */}
        <Box sx={{ display: "flex", gap: 1 }}>
          <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
            <InputLabel>Month</InputLabel>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              label="Month"
            >
              {months.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ bgcolor: "white", borderRadius: 1, minWidth: 200 }}>
            <InputLabel>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              label="Year"
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Export Button */}
          <Button
            variant="contained"
            size="small"
            onClick={handleExportToExcel}
            sx={{
              height: 40,
              backgroundColor: '#2e7d32', // light red
              color: 'white',
              '&:hover': {
                backgroundColor: '#1b5e20', // slightly darker red on hover
              },
            }}
          >
            Export to Excel
          </Button>
        </Box>

        {/* Total Count */}
        <Typography
          variant="h6"
          component="h2"
          fontWeight="500"
          sx={{ whiteSpace: "nowrap" }}
        >
          Total Count: {departureDetails.length}
        </Typography>
      </Box>


      {/* Table Section */}
      <Paper elevation={3} sx={{ p: 2 }}>
        {filteredCocData.length > 0 ? (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  Departure Details
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 150 }}>
                  CrewWelfare
                </TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 250 }}>
                  Sea Service Request
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCocData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          <strong style={{ color: "black" }}>
                            Vessel Name: {item.contractDetails?.ShipDetails?.vesselname || "N/A"}
                          </strong>
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          <strong style={{ color: "#2e7d32" }}>
                            Company Name: {item.contractDetails?.vesselOwner?.company_shortname || "N/A"}
                          </strong>
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          marginTop: 1,
                        }}
                      >
                        <Chip
                          size="small"
                          label={`Crew: ${item.contractDetails?.crewdetails?.name || "N/A"
                            }`}
                          sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                        />
                        <Chip
                          size="small"
                          label={`Indos No.: ${item.contractDetails?.crewdetails?.indosno || "N/A"
                            }`}
                          sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                        />
                        <Chip
                          size="small"
                          label={`Agent.: ${item.contractDetails?.crewingAgent?.agentName || "N/A"
                            }`}
                          sx={{ bgcolor: "#f3e5f5", color: "#6a1b9a" }}
                        />
                        <Chip
                          size="small"
                          label={`Port Name: ${item.contractStatusAfterForm1?.departure?.[0]?.portName ||
                            "N/A"
                            }`}
                          sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }}
                        />
                        {/* <Chip
                          size="small"
                          label={`Arrival Date: ${item.contractStatusAfterForm1?.departure?.[0]?.date
                            ? new Date(
                              item.contractStatusAfterForm1.departure[0].date
                            ).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                            : "N/A"
                            }`}
                          sx={{ bgcolor: "#fff3e0", color: "#e65100" }}
                        /> */}

                        {/* ✅ Show editable or view mode for COC Date */}
                        {editingArrivalId === item?.contractStatusAfterForm1?._id &&
                          userEmail === "neeturashmi@gmail.com" ? (
                          <>
                            <TextField
                              type="date"
                              value={newArrivalDate}
                              onChange={(e) => setNewArrivalDate(e.target.value)}
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              sx={{ mr: 1 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => ChangeArrivalDate(item?.contractStatusAfterForm1?._id)}
                              disabled={!newArrivalDate}
                            >
                              Save
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ ml: 1 }}
                              onClick={() => {
                                setEditingArrivalId(null);
                                setNewArrivalDate("");
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Chip
                              size="small"
                              label={`Arrival Date: ${item?.contractStatusAfterForm1?.departure?.[0]?.date
                                ? new Date(item?.contractStatusAfterForm1.departure[0].date)
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  })
                                : "N/A"
                                }`}
                              sx={{ bgcolor: "#fff3e0", color: "#e65100" }}
                            />
                            {userEmail === "neeturashmi@gmail.com" && (
                              <Tooltip title="Edit SignOff Date">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setEditingArrivalId(item?.contractStatusAfterForm1?._id);
                                    setNewArrivalDate(
                                      item?.contractStatusAfterForm1?.signOff?.[0]?.date
                                        ? new Date(item.contractStatusAfterForm1.signOff[0].date)
                                          .toISOString()
                                          .split("T")[0]
                                        : ""
                                    );
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}

                        {item.approvedSeaService ? (
                          <Chip
                            label="Approved Sea Service Request"
                            sx={{
                              bgcolor: "#c8e6c9",
                              color: "#2e7d32",
                              fontWeight: "bold",
                            }}
                          />
                        ) : (
                          <Chip
                            label="Sea Service Request Not Approved"
                            sx={{
                              bgcolor: "#ffcdd2",
                              color: "#c62828",
                              fontWeight: "bold",
                            }}
                          />
                        )}

                        {/* {item?.contractStatusAfterForm1?.departure?.[0]?.creatorIdWithName?.[0] && (
                          <Chip
                            size="small"
                            label={`Added Arrival: ${item.contractStatusAfterForm1.departure[0].creatorIdWithName[0].name} - ${new Date(item.contractStatusAfterForm1.departure[0].creatorIdWithName[0].createdAt).toLocaleDateString('en-GB').replace(/\//g, '-')}`}
                            sx={{ bgcolor: "#ffebee", color: "#c62828" }}
                          />
                        )} */}
                        {item?.contractStatusAfterForm1?.departure?.[0]?.creatorIdWithName?.length > 0 && (
                          <Chip
                            size="small"
                            label={`Added SignOff: ${item.contractStatusAfterForm1.departure[0].creatorIdWithName[
                              item.contractStatusAfterForm1.departure[0].creatorIdWithName.length - 1
                            ].name
                              } - ${new Date(
                                item.contractStatusAfterForm1.departure[0].creatorIdWithName[
                                  item.contractStatusAfterForm1.departure[0].creatorIdWithName.length - 1
                                ].createdAt
                              )
                                .toLocaleDateString("en-GB")
                                .replace(/\//g, "-")}`}
                            sx={{ bgcolor: "#ffebee", color: "#c62828" }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={() => fetchDGCharges(item.contractDetails?._id)}
                      >
                        <AttachMoneyIcon />
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <button
                          style={{
                            backgroundColor: "#1976d2",
                            color: "white",
                            border: "none",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            const crewId =
                              item.contractDetails?.crewdetails?._id;
                             const contractId = item.contractDetails?._id;

                            if (crewId) {
                              setPdfUrl(
                                `${baseURL}/api/pdf/sea-service/${crewId}?contractId=${contractId}`
                                // `http://localhost:8000/api/pdf/sea-service/${crewId}`

                              );
                              setOpenPdfModal(true);
                            } else {
                              toast.error("Crew ID not found.");
                            }
                          }}
                        >
                          View
                        </button>
                        <Button
                          variant="outlined"
                          disabled={loadingCrewId === item.contractDetails?.crewdetails?._id}
                          onClick={() =>
                            handleSendRequest(item.contractDetails?.crewdetails?._id)
                          }
                          startIcon={
                            loadingCrewId === item.contractDetails?.crewdetails?._id ? (
                              <CircularProgress size={16} />
                            ) : null
                          }
                        >
                          {loadingCrewId === item.contractDetails?.crewdetails?._id
                            ? "Sending..."
                            : "Send Request"}
                        </Button>

                 <Button 
  onClick={() => navigate(`/SeaServiceProfile/${item.contractDetails?.crewingAgent?._id}`)} 
  variant="text" 
  color="primary"
>
  Form Sea Service
</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        ) : (
          <Typography color="textSecondary" align="center">
            No departure details found.
          </Typography>
        )}
      </Paper>

      <Dialog
        open={openPdfModal}
        onClose={() => setOpenPdfModal(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            height: "90vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogTitle>
          Sea Service PDF
          <IconButton
            aria-label="close"
            onClick={() => setOpenPdfModal(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, flexGrow: 1, overflow: "hidden" }}>
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: "none" }}
            title="PDF Viewer"
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={openChargesDialog}
        onClose={() => setOpenChargesDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          DG Charges Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenChargesDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dgChargesData ? (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body1">
                <strong>Sign On Date:</strong>{" "}
                {new Date(dgChargesData.signOnDate).toLocaleDateString("en-GB")}
              </Typography>
              <Typography variant="body1">
                <strong>Sign Off Date:</strong>{" "}
                {new Date(dgChargesData.signOffDate).toLocaleDateString("en-GB")}
              </Typography>
              <Typography variant="h6" color="primary">
                <strong>Total DG Charges:</strong> ₹ {dgChargesData.exactTotal}
              </Typography>
            </Box>
          ) : (
            <Typography>No charges data available.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <TablePagination
        component="div"
        count={filteredCocData.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          px: 2,
          bgcolor: "#fafafa",
          borderTop: "1px solid #e0e0e0",
          display: "flex",
          justifyContent: "flex-end"
        }}
      />

      <ToastContainer />
    </Box>
  );
};

export default DepartureModal;
